import clsx from 'clsx';
import { ShopperProductsTypes } from 'commerce-sdk-isomorphic';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { A11y, Navigation, Thumbs } from 'swiper/modules';
import { Swiper as ReactSwiper, SwiperSlide as ReactSwiperSlide } from 'swiper/react';
import { Swiper } from 'swiper/types';
import { usePDPActions, usePDPData } from '~/features/product-details/context';
import ChevronDown from '~/icons/chevron-down.svg';
import ChevronLeft from '~/icons/chevron-left.svg';
import ChevronUp from '~/icons/chevron-up.svg';
import ChevronRight from '~/icons/chevron.svg';
import Play from '~/icons/playbtn.svg';
import { SalesforceProduct } from '~/lib/salesforce/types';
import { DynamicImageLoader } from '~/shared/components/DynamicImageLoader';
import Modal from '~/shared/components/Modal';
import { Video } from '~/shared/components/Video';
import { useVideoMetadata } from '~/shared/components/Video/utils';
import { breakpointsRaw } from '~/theme/breakpoints';
import { ProductInfoLabelDialog } from './ProductInfoLabelDialog';
import { SwiperVideo } from './SwiperVideo';
import { useProductThumbnailLabels } from './hooks/useProductThumbnailLabels';
import { useSwiperNavigation } from './hooks/useSwiperNavigation';
import { isVideo, Media } from './media';
import styles from './ProductGallery.module.scss';

import 'swiper/css';

const ProductGalleryLightbox = dynamic(
    () => import('./ProductGalleryLightbox').then((module) => module.ProductGalleryLightbox),
    { ssr: false },
);

interface Props extends React.HTMLAttributes<HTMLDivElement> {
    images: ShopperProductsTypes.Image[];
    videos?: string[];
}

/*
 * Measured against the container's max width: the main image tops out at 739px when the
 * thumbnail strip takes a column beside it, and at 900px when a lone image has the row to
 * itself. Below `md` the gallery is full width, and the thumbnail strip is hidden below `sm`.
 */
const SLIDER_MEDIA_SIZES = '(min-width: 1024px) 50vw, (min-width: 768px) 85vw, 100vw';
const SINGLE_MEDIA_SIZES = '(min-width: 1024px) 70vw, 100vw';
const THUMBNAIL_SIZES = '(min-width: 1024px) 236px, 128px';

const GALLERY_IMAGE_QUALITY = 80;

const getImageAlt = (image: ShopperProductsTypes.Image, productName?: string) =>
    image.alt || image.title || productName || '';

const withoutLetterboxing = (thumbnailSrc: string) =>
    thumbnailSrc.replace(/\/(?:default|hqdefault|sddefault)\.jpg(\?.*)?$/, '/mqdefault.jpg$1');

const VideoThumbnail: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
    const { metadata } = useVideoMetadata(src);

    return (
        <>
            {metadata?.thumbnailSrc && (
                /**
                 * Provider thumbnails are served from hosts that `next/image` is not
                 * configured for, so this stays a plain image.
                 */
                // eslint-disable-next-line @next/next/no-img-element
                <img
                    className={styles.videoThumbnail}
                    src={withoutLetterboxing(metadata.thumbnailSrc)}
                    alt={alt}
                    loading="lazy"
                    decoding="async"
                />
            )}

            <Play className={styles.playIcon} />
        </>
    );
};

export const ProductGallery: React.FC<Props> = ({ images, videos, children, ...props }) => {
    const { product } = usePDPData();
    const { setInfoLabelDialog } = usePDPActions();

    const [galleryIndex, setGalleryIndex] = useState(0);
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);

    /**
     * Where the lightbox has got to. A ref rather than state, so its swiping does not re-render
     * the slider beneath it and the slider is only moved once, on close - which is also what
     * keeps a video playing in the lightbox from being joined by the copy behind it.
     */
    const lightboxIndex = useRef(0);

    const onSlideClick = useCallback((index: number) => {
        lightboxIndex.current = index;
        setGalleryIndex(index);
        setIsGalleryOpen(true);
    }, []);

    const onSlideChange = useCallback((index: number) => {
        setGalleryIndex(index);
    }, []);

    const onGalleryClose = useCallback(() => {
        setIsGalleryOpen(false);
        setGalleryIndex(lightboxIndex.current);
    }, []);

    const media = useMemo(() => [...images, ...(videos || [])], [images, videos]);

    // Reset lightbox index when switching product IDs.
    useEffect(() => {
        setGalleryIndex(0);
    }, [product.id]);

    if (!media?.length) return null;

    // The elements that go on top of our gallery.
    const foregroundElements = (
        <>
            {children}

            <ProductInfoLabelDialog onClose={() => setInfoLabelDialog(undefined)} />
        </>
    );

    return (
        <>
            {media.length === 1 ? (
                <ProductMedia
                    product={product}
                    media={media}
                    {...props}
                    onClick={() => onSlideClick(0)}
                >
                    {foregroundElements}
                </ProductMedia>
            ) : (
                <ProductMediaSlider
                    product={product}
                    media={media}
                    {...props}
                    activeIndex={galleryIndex}
                    onSlideChange={onSlideChange}
                    onSlideClick={onSlideClick}
                >
                    {foregroundElements}
                </ProductMediaSlider>
            )}

            {isGalleryOpen && (
                <Modal variant="gallery" isOpen showCloseIcon handleClose={onGalleryClose}>
                    <ProductGalleryLightbox
                        media={media}
                        selectedIndex={galleryIndex}
                        productName={product.name}
                        onIndexChange={(index) => (lightboxIndex.current = index)}
                    />
                </Modal>
            )}
        </>
    );
};

interface ProductMediaProps extends React.HTMLAttributes<HTMLDivElement> {
    product: SalesforceProduct;
    media: Media[];
}

const ProductMedia: React.FC<ProductMediaProps> = ({
    className,
    children,
    media,
    product,
    ...rest
}) => {
    // ASSERTION: If we're using this component, we're guaranteed to have 1 item to present.
    const itemToShow = media.at(0)!;

    return (
        <div className={clsx(styles.productMedia, className)} {...rest}>
            <div className={styles.productMediaWrapper}>
                <div className={styles.media}>
                    {isVideo(itemToShow) ? (
                        <Video src={itemToShow} />
                    ) : (
                        <DynamicImageLoader
                            className={styles.image}
                            src={itemToShow.link}
                            fill
                            sizes={SINGLE_MEDIA_SIZES}
                            quality={GALLERY_IMAGE_QUALITY}
                            alt={getImageAlt(itemToShow, product.name)}
                            title={itemToShow.title}
                            priority
                        />
                    )}
                </div>

                {children}
            </div>
        </div>
    );
};

interface ProductMediaSliderProps extends React.HTMLAttributes<HTMLDivElement> {
    product: SalesforceProduct;
    media: Media[];
    /** Where the slider should be. Driven by the lightbox when it closes. */
    activeIndex?: number;
    onSlideChange?(index: number): void;
    onSlideClick?(index: number): void;
}

const ProductMediaSlider: React.FC<ProductMediaSliderProps> = ({
    product,
    media,
    children,
    className,
    activeIndex,
    onSlideChange,
    onSlideClick,
    ...rest
}) => {
    const [thumbsSwiper, setThumbsSwiper] = useState<Swiper | null>(null);
    const {
        navigation: thumbsNavigation,
        prevRef: thumbsPrevRef,
        nextRef: thumbsNextRef,
    } = useSwiperNavigation();

    const [mediaSwiper, setMediaSwiper] = useState<Swiper | null>(null);
    const {
        navigation: mediaNavigation,
        prevRef: mediaPrevRef,
        nextRef: mediaNextRef,
    } = useSwiperNavigation();

    const thumbnailLabels = useProductThumbnailLabels(media);

    // Flag to identify if a "slide to" was caused by a shift in focus.
    const [isSlideFromFocus, setIsSlideFromFocus] = useState(false);

    // Catching up to a change that came from the slider itself lands on the index it already
    // holds, and `slideTo` does nothing there - so only the lightbox ever actually moves it.
    useEffect(() => {
        if (activeIndex !== undefined) mediaSwiper?.slideTo(activeIndex);
    }, [activeIndex, mediaSwiper]);

    const onThumbsSlideClick = (thumbIndex: number, event: React.MouseEvent<HTMLElement>) => {
        const target = event.currentTarget;

        mediaSwiper?.slideTo(thumbIndex);

        target.focus();

        // If "slide to" came from a click, set this tag to false.
        setIsSlideFromFocus(false);
    };

    const onThumbsSlideFocus = (thumbIndex: number) => {
        // If "slide to" came from a click, set this tag to true.
        // Then, slide to the index that was focused.
        setIsSlideFromFocus(true);
        mediaSwiper?.slideTo(thumbIndex);
    };

    const onSwiperChange = useCallback(
        (swiper: Swiper) => {
            onSlideChange?.(swiper.activeIndex);
        },
        [onSlideChange],
    );

    // Reset media gallery index when switching product variants.
    useEffect(() => {
        mediaSwiper?.slideTo(0);
    }, [product.id]);

    return (
        <div className={clsx(styles.productMediaSlider, className)} {...rest}>
            <div className={styles.thumbsSlider}>
                <ReactSwiper
                    className={styles.thumbsSliderSwiper}
                    wrapperClass={styles.thumbsSliderWrapper}
                    direction="vertical"
                    slidesPerView="auto"
                    navigation={thumbsNavigation}
                    watchSlidesProgress
                    enabled={false}
                    breakpoints={{
                        [breakpointsRaw.sm]: {
                            enabled: true,
                        },
                    }}
                    modules={[Thumbs, Navigation]}
                    onSwiper={setThumbsSwiper}
                >
                    {media.map((item, index) => (
                        <ReactSwiperSlide
                            tag="button"
                            key={isVideo(item) ? item : item.link}
                            className={styles.thumbsSliderItem}
                            aria-label={thumbnailLabels[index]}
                            aria-current={index === activeIndex || undefined}
                            onClick={(event) => onThumbsSlideClick(index, event)}
                            onFocus={() => onThumbsSlideFocus(index)}
                        >
                            <div className={styles.media}>
                                {isVideo(item) ? (
                                    <VideoThumbnail src={item} alt={product.name ?? ''} />
                                ) : (
                                    <DynamicImageLoader
                                        className={styles.image}
                                        src={item.link}
                                        alt={getImageAlt(item, product.name)}
                                        fill
                                        sizes={THUMBNAIL_SIZES}
                                    />
                                )}
                            </div>
                        </ReactSwiperSlide>
                    ))}
                </ReactSwiper>

                <button
                    ref={thumbsPrevRef}
                    className={clsx(styles.navigation, styles.thumbsSliderPrev)}
                    aria-label="Forrige"
                >
                    <ChevronUp className={styles.navigationIcon} aria-hidden />
                </button>

                <button
                    ref={thumbsNextRef}
                    className={clsx(styles.navigation, styles.thumbsSliderNext)}
                    aria-label="Næste"
                >
                    <ChevronDown className={styles.navigationIcon} aria-hidden />
                </button>
            </div>

            <div className={styles.mediaSlider}>
                <ReactSwiper
                    className={styles.mediaSliderSwiper}
                    wrapperClass={styles.mediaSliderWrapper}
                    thumbs={{
                        swiper: thumbsSwiper,
                    }}
                    navigation={mediaNavigation}
                    breakpoints={{
                        [breakpointsRaw.sm]: {
                            allowTouchMove: false,
                            speed: 0,
                        },
                    }}
                    modules={[Thumbs, A11y, Navigation]}
                    onSwiper={setMediaSwiper}
                    onSlideChange={onSwiperChange}
                >
                    {media.map((item, index) => (
                        <ReactSwiperSlide
                            key={isVideo(item) ? item : item.link}
                            className={styles.mediaSliderItem}
                        >
                            <div className={styles.media}>
                                {isVideo(item) ? (
                                    <SwiperVideo src={item} playOnActive={!isSlideFromFocus} />
                                ) : (
                                    <DynamicImageLoader
                                        className={styles.image}
                                        src={item.link}
                                        fill
                                        sizes={SLIDER_MEDIA_SIZES}
                                        quality={GALLERY_IMAGE_QUALITY}
                                        alt={getImageAlt(item, product.name)}
                                        title={item.title}
                                        onClick={() => onSlideClick?.(index)}
                                        priority={index === 0}
                                        loading={index !== 0 ? 'lazy' : undefined}
                                    />
                                )}
                            </div>
                        </ReactSwiperSlide>
                    ))}
                </ReactSwiper>

                <button
                    ref={mediaPrevRef}
                    className={clsx(styles.navigation, styles.mediaSliderPrev)}
                    aria-label="Forrige billede"
                >
                    <ChevronLeft className={styles.navigationIcon} aria-hidden />
                </button>

                <button
                    ref={mediaNextRef}
                    className={clsx(styles.navigation, styles.mediaSliderNext)}
                    aria-label="Næste billede"
                >
                    <ChevronRight className={styles.navigationIcon} aria-hidden />
                </button>

                {children}
            </div>
        </div>
    );
};
